import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2, Droplets, Truck, Container, Package, Bus, Car, MoreHorizontal,
  IdCard, Users, ShieldCheck, Wrench, Plus, Unlink, BookOpen, FileBadge,
} from 'lucide-react';
import { LedgerView } from '@/components/ledgers/LedgerView';
import { EntityDocumentsPanel, VEHICLE_SLOTS } from '@/components/documents/EntityDocumentsPanel'; // PR-DOCVAULT JUN03
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { AssignmentSheet } from '@/components/common/AssignmentSheet';
import type {
  Vehicle, VehicleInput, VehicleType, VehicleStatus, PlateType,
  FuelType, TransmissionType, NaqliatScope, InspectionStatus,
} from '@/lib/types';
import { useLegalEntities, useAllInvestors } from '@/hooks/useVehicles';
import { useAvailableBodies, useAssignBody, useUnassignBody } from '@/hooks/useBodies';
import { useOperationalProjects } from '@/hooks/useProjects';
import { PlateInput } from '@/components/PlateInput';
import { ExpiryIndicator } from '@/components/ComplianceBadge';
import { Tabs } from '@/components/Tabs';
import { validatePlate } from '@/lib/plate';
import { AuthedImage } from '@/components/common/AuthedImage';

interface VehicleFormProps {
  initial?: Vehicle | null;
  onSubmit: (input: VehicleInput) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

const VEHICLE_TYPES: { value: VehicleType; icon: typeof Droplets }[] = [
  { value: 'water_tanker', icon: Droplets },
  { value: 'sewage_tanker', icon: Droplets },
  { value: 'dumper', icon: Truck },
  { value: 'container_truck', icon: Container },
  { value: 'flatbed', icon: Package },
  { value: 'pickup', icon: Truck },
  { value: 'bus', icon: Bus },
  { value: 'other', icon: MoreHorizontal },
];

const PLATE_TYPES: PlateType[] = [
  'private', 'commercial', 'public_transport', 'taxi',
  'temporary', 'government', 'diplomatic', 'export', 'other',
];

const STATUSES: VehicleStatus[] = ['active', 'inactive', 'under_maintenance', 'sold'];
const FUEL_TYPES: FuelType[] = ['diesel', 'gasoline', 'lpg', 'electric', 'hybrid'];
const TRANSMISSIONS: TransmissionType[] = ['manual', 'automatic'];
const NAQLIAT_SCOPES: NaqliatScope[] = ['city_only', 'inter_city', 'cross_border'];
const INSPECTION_STATUSES: InspectionStatus[] = ['valid', 'expired', 'pending', 'failed'];

const blank: VehicleInput = {
  plateDigits: '',
  plateLettersEn: '',
  plateLettersAr: '',
  plateType: 'commercial',
  legalEntityId: '',
  investorId: '',
  projectId: null,
  vehicleType: 'water_tanker',
  brand: '',
  model: '',
  year: null,
  purchaseDate: '',
  purchasePriceSar: null,
  targetTonnageM3: null,
  afaqyDeviceId: '',
  petroappCardId: '',
  istimaraNumber: '',
  istimaraExpiry: '',
  insuranceCompanyName: '',
  insurancePolicyNumber: '',
  insuranceExpiry: '',
  naqliatCardNumber: '',
  naqliatCardIssueDate: '',
  naqliatCardExpiryDate: '',
  naqliatScope: null,
  inspectionCertificateNumber: '',
  inspectionLastDate: '',
  inspectionNextDueDate: '',
  inspectionStationName: '',
  inspectionStatus: null,
  vin: '',
  engineNumber: '',
  fuelType: 'diesel',
  transmission: 'manual',
  axlesCount: null,
  gvwrKg: null,
  payloadCapacityKg: null,
  manufacturingCountry: '',
  colorOfficial: '',
  currentOdometerKm: 0,
  status: 'active',
  notes: '',
};

export function VehicleForm({ initial, onSubmit, onCancel, submitLabel }: VehicleFormProps) {
  const { t } = useTranslation();
  const { data: legalEntities = [] } = useLegalEntities();
  const { data: investors = [] } = useAllInvestors();
  const { data: projects = [] } = useOperationalProjects();
  const { data: availableBodies = [] } = useAvailableBodies();
  const assignMutation = useAssignBody();
  const unassignMutation = useUnassignBody();

  const [form, setForm] = useState<VehicleInput>(blank);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAssignBody, setShowAssignBody] = useState(false);
  const [selectedBodyId, setSelectedBodyId] = useState<string>('');

  useEffect(() => {
    if (initial) {
      setForm({
        plateDigits: initial.plateDigits ?? '',
        plateLettersEn: initial.plateLettersEn ?? '',
        plateLettersAr: initial.plateLettersAr ?? '',
        plateType: initial.plateType ?? 'commercial',
        legalEntityId: initial.legalEntityId,
        investorId: initial.investorId,
        projectId: (initial as Vehicle & { projectId?: string | null }).projectId ?? null,
        vehicleType: initial.vehicleType,
        brand: initial.brand ?? '',
        model: initial.model ?? '',
        year: initial.year ?? null,
        purchaseDate: initial.purchaseDate ? initial.purchaseDate.slice(0, 10) : '',
        purchasePriceSar: initial.purchasePriceSar != null ? Number(initial.purchasePriceSar) : null,
        targetTonnageM3: initial.targetTonnageM3 != null ? Number(initial.targetTonnageM3) : null,
        afaqyDeviceId: initial.afaqyDeviceId ?? '',
        petroappCardId: initial.petroappCardId ?? '',
        istimaraNumber: initial.istimaraNumber ?? '',
        istimaraExpiry: initial.istimaraExpiry ? initial.istimaraExpiry.slice(0, 10) : '',
        insuranceCompanyName: initial.insuranceCompanyName ?? '',
        insurancePolicyNumber: initial.insurancePolicyNumber ?? '',
        insuranceExpiry: initial.insuranceExpiry ? initial.insuranceExpiry.slice(0, 10) : '',
        naqliatCardNumber: initial.naqliatCardNumber ?? '',
        naqliatCardIssueDate: initial.naqliatCardIssueDate ? initial.naqliatCardIssueDate.slice(0, 10) : '',
        naqliatCardExpiryDate: initial.naqliatCardExpiryDate ? initial.naqliatCardExpiryDate.slice(0, 10) : '',
        naqliatScope: initial.naqliatScope,
        inspectionCertificateNumber: initial.inspectionCertificateNumber ?? '',
        inspectionLastDate: initial.inspectionLastDate ? initial.inspectionLastDate.slice(0, 10) : '',
        inspectionNextDueDate: initial.inspectionNextDueDate ? initial.inspectionNextDueDate.slice(0, 10) : '',
        inspectionStationName: initial.inspectionStationName ?? '',
        inspectionStatus: initial.inspectionStatus,
        vin: initial.vin ?? '',
        engineNumber: initial.engineNumber ?? '',
        fuelType: initial.fuelType ?? 'diesel',
        transmission: initial.transmission ?? 'manual',
        axlesCount: initial.axlesCount,
        gvwrKg: initial.gvwrKg,
        payloadCapacityKg: initial.payloadCapacityKg,
        manufacturingCountry: initial.manufacturingCountry ?? '',
        colorOfficial: initial.colorOfficial ?? '',
        currentOdometerKm: initial.currentOdometerKm,
        status: initial.status,
        notes: initial.notes ?? '',
      });
    } else {
      const defaultLE = legalEntities.find((le) => le.isOperational) ?? legalEntities[0];
      setForm({ ...blank, legalEntityId: defaultLE?.id ?? '' });
    }
  }, [initial, legalEntities]);

  const update = <K extends keyof VehicleInput>(key: K, value: VehicleInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const isWaterOrSewage = form.vehicleType === 'water_tanker' || form.vehicleType === 'sewage_tanker';

  // Live plate validation for tab badge
  const plateValidation = validatePlate({
    digits: form.plateDigits,
    lettersEn: form.plateLettersEn,
    lettersAr: form.plateLettersAr,
  });

  const currentBody = initial?.bodyAssignments?.[0]?.body;
  const currentBodyOwner = initial?.bodyAssignments?.[0]?.body?.ownerInvestor;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!plateValidation.valid) {
      // Say exactly which part is wrong. Previously this was a generic message
      // behind a disabled button, which read as "the save button is broken".
      const reasons = [
        plateValidation.digits.error && `${t('plate.digits')}: ${plateValidation.digits.error}`,
        plateValidation.lettersEn.error && `${t('plate.lettersEn')}: ${plateValidation.lettersEn.error}`,
        plateValidation.lettersAr.error && `${t('plate.lettersAr')}: ${plateValidation.lettersAr.error}`,
      ].filter(Boolean);
      setError(reasons.length ? reasons.join(' · ') : t('vehicles.errors.invalidPlate'));
      return;
    }
    if (!form.investorId) {
      setError(t('vehicles.errors.investorRequired'));
      return;
    }
    if (!form.legalEntityId) {
      setError(t('vehicles.errors.legalEntityRequired'));
      return;
    }

    // Build payload - send only non-empty fields
    const payload: VehicleInput = {
      plateDigits: form.plateDigits,
      plateLettersEn: form.plateLettersEn,
      legalEntityId: form.legalEntityId,
      investorId: form.investorId,
      vehicleType: form.vehicleType,
    };
    if (form.plateLettersAr) payload.plateLettersAr = form.plateLettersAr;
    if (form.plateType) payload.plateType = form.plateType;
    if (form.brand?.trim()) payload.brand = form.brand.trim();
    if (form.model?.trim()) payload.model = form.model.trim();
    if (form.year != null) payload.year = form.year;
    if (form.purchaseDate?.trim()) payload.purchaseDate = form.purchaseDate.trim();
    if (form.purchasePriceSar != null) payload.purchasePriceSar = form.purchasePriceSar;
    if (isWaterOrSewage && form.targetTonnageM3 != null) payload.targetTonnageM3 = form.targetTonnageM3;
    if (form.afaqyDeviceId?.trim()) payload.afaqyDeviceId = form.afaqyDeviceId.trim();
    if (form.petroappCardId?.trim()) payload.petroappCardId = form.petroappCardId.trim();
    if (form.istimaraNumber?.trim()) payload.istimaraNumber = form.istimaraNumber.trim();
    if (form.istimaraExpiry?.trim()) payload.istimaraExpiry = form.istimaraExpiry.trim();
    if (form.insuranceCompanyName?.trim()) payload.insuranceCompanyName = form.insuranceCompanyName.trim();
    if (form.insurancePolicyNumber?.trim()) payload.insurancePolicyNumber = form.insurancePolicyNumber.trim();
    if (form.insuranceExpiry?.trim()) payload.insuranceExpiry = form.insuranceExpiry.trim();
    if (form.naqliatCardNumber?.trim()) payload.naqliatCardNumber = form.naqliatCardNumber.trim();
    if (form.naqliatCardIssueDate?.trim()) payload.naqliatCardIssueDate = form.naqliatCardIssueDate.trim();
    if (form.naqliatCardExpiryDate?.trim()) payload.naqliatCardExpiryDate = form.naqliatCardExpiryDate.trim();
    if (form.naqliatScope) payload.naqliatScope = form.naqliatScope;
    if (form.inspectionCertificateNumber?.trim()) payload.inspectionCertificateNumber = form.inspectionCertificateNumber.trim();
    if (form.inspectionLastDate?.trim()) payload.inspectionLastDate = form.inspectionLastDate.trim();
    if (form.inspectionNextDueDate?.trim()) payload.inspectionNextDueDate = form.inspectionNextDueDate.trim();
    if (form.inspectionStationName?.trim()) payload.inspectionStationName = form.inspectionStationName.trim();
    if (form.inspectionStatus) payload.inspectionStatus = form.inspectionStatus;
    if (form.vin?.trim()) payload.vin = form.vin.trim();
    if (form.engineNumber?.trim()) payload.engineNumber = form.engineNumber.trim();
    if (form.fuelType) payload.fuelType = form.fuelType;
    if (form.transmission) payload.transmission = form.transmission;
    if (form.axlesCount != null) payload.axlesCount = form.axlesCount;
    if (form.gvwrKg != null) payload.gvwrKg = form.gvwrKg;
    if (form.payloadCapacityKg != null) payload.payloadCapacityKg = form.payloadCapacityKg;
    if (form.manufacturingCountry?.trim()) payload.manufacturingCountry = form.manufacturingCountry.trim();
    if (form.colorOfficial?.trim()) payload.colorOfficial = form.colorOfficial.trim();
    if (form.currentOdometerKm != null) payload.currentOdometerKm = form.currentOdometerKm;
    if (form.status) payload.status = form.status;
    if (form.notes?.trim()) payload.notes = form.notes.trim();
    // PR MAY10 P4B-POLISH (A.6)
    if (form.projectId !== undefined) payload.projectId = form.projectId ?? null;

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignBody = async () => {
    if (!initial?.id || !selectedBodyId) return;
    try {
      await assignMutation.mutateAsync({ bodyId: selectedBodyId, vehicleId: initial.id });
      setShowAssignBody(false);
      setSelectedBodyId('');
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleUnassignBody = async () => {
    if (!initial?.id) return;
    if (!confirm(t('bodies.confirmUnassign'))) return;
    try {
      await unassignMutation.mutateAsync(initial.id);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  // ---------- Tab content components ----------

  const IdentityTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Vehicle type pills */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.type')}
        </legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {VEHICLE_TYPES.map((vt) => {
            const Icon = vt.icon;
            const selected = form.vehicleType === vt.value;
            return (
              <button
                key={vt.value}
                type="button"
                onClick={() => update('vehicleType', vt.value)}
                className={`flex flex-col items-center gap-1 p-3 rounded-md border text-xs font-medium transition-all ${
                  selected
                    ? 'border-gold bg-gold/10 text-foreground'
                    : 'border-border hover:border-muted-foreground/40 text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className={`h-5 w-5 ${selected ? 'text-gold' : ''}`} />
                <span>{t(`vehicles.type.${vt.value}`)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Smart plate input */}
      <PlateInput
        digits={form.plateDigits}
        lettersEn={form.plateLettersEn}
        lettersAr={form.plateLettersAr ?? ''}
        onChange={({ digits, lettersEn, lettersAr }) => {
          setForm((f) => ({ ...f, plateDigits: digits, plateLettersEn: lettersEn, plateLettersAr: lettersAr }));
        }}
        required
      />

      {/* Plate type */}
      <Field label={t('vehicles.field.plateType')}>
        <Select value={form.plateType ?? 'commercial'} onChange={(v) => update('plateType', v as PlateType)}>
          {PLATE_TYPES.map((pt) => (
            <option key={pt} value={pt}>{t(`vehicles.plateType.${pt}`)}</option>
          ))}
        </Select>
      </Field>

      {/* Identity */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.identity')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.brand')}>
            <Input value={form.brand ?? ''} onChange={(e) => update('brand', e.target.value)} placeholder="Mercedes, Volvo..." />
          </Field>
          <Field label={t('vehicles.field.model')}>
            <Input value={form.model ?? ''} onChange={(e) => update('model', e.target.value)} placeholder="Actros, FH..." />
          </Field>
          <Field label={t('vehicles.field.year')}>
            <Input
              type="number" min={1980} max={2100} dir="ltr"
              value={form.year ?? ''}
              onChange={(e) => update('year', e.target.value ? Number(e.target.value) : null)}
            />
          </Field>
          <Field label={t('vehicles.field.status')}>
            <Select value={form.status ?? 'active'} onChange={(v) => update('status', v as VehicleStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{t(`vehicles.status.${s}`)}</option>)}
            </Select>
          </Field>
          <Field label={t('vehicles.field.colorOfficial')}>
            <Input value={form.colorOfficial ?? ''} onChange={(e) => update('colorOfficial', e.target.value)} placeholder={t('vehicles.placeholder.color')} />
          </Field>
          <Field label={t('vehicles.field.manufacturingCountry')}>
            <Input value={form.manufacturingCountry ?? ''} onChange={(e) => update('manufacturingCountry', e.target.value)} placeholder="Germany, Sweden..." />
          </Field>
        </div>
      </fieldset>

      {/* Operations */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.operations')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.currentOdometerKm')}>
            <Input
              type="number" min={0} dir="ltr"
              value={form.currentOdometerKm ?? 0}
              onChange={(e) => update('currentOdometerKm', e.target.value ? Number(e.target.value) : 0)}
              className="font-mono"
            />
          </Field>
          {isWaterOrSewage && (
            <Field label={t('vehicles.field.targetTonnageM3')}>
              <Input
                type="number" step="0.5" min={0} max={100} dir="ltr"
                value={form.targetTonnageM3 ?? ''}
                onChange={(e) => update('targetTonnageM3', e.target.value ? Number(e.target.value) : null)}
                placeholder="28"
              />
            </Field>
          )}
        </div>
      </fieldset>
    </div>
  );

  const OwnershipTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.ownership')}
        </legend>
        <Field label={t('vehicles.field.investor')} required>
          <Select
            value={form.investorId ?? ''}
            onChange={(v) => update('investorId', v)}
            placeholder={t('vehicles.placeholder.selectInvestor')}
          >
            {investors.map((inv) => (
              <option key={inv.id} value={inv.id}>{inv.code} — {inv.fullNameEn}</option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground mt-1">{t('vehicles.help.investorOwnsTrack')}</p>
        </Field>
        <Field label={t('vehicles.field.legalEntity')} required>
          <Select
            value={form.legalEntityId ?? ''}
            onChange={(v) => update('legalEntityId', v)}
            placeholder={t('vehicles.placeholder.selectLegalEntity')}
          >
            {legalEntities.map((le) => (
              <option key={le.id} value={le.id}>{le.code} — {le.nameEn}</option>
            ))}
          </Select>
        </Field>
        {/* PR MAY10 P4B-POLISH (A.6) — operational project */}
        <Field label={t('vehicles.field.project', { defaultValue: 'Operational project' })}>
          <Select
            value={form.projectId ?? ''}
            onChange={(v) => update('projectId', v || null)}
            placeholder={t('vehicles.placeholder.selectProject', { defaultValue: '— No project —' })}
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.code} — {p.name_en}</option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            {t('vehicles.help.project', { defaultValue: 'Used for project-scoped filters across the app.' })}
          </p>
        </Field>
      </fieldset>

      {/* Body assignment section - only shown for existing vehicles */}
      {initial && (
        <fieldset className="space-y-3 pt-3 border-t border-border">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('vehicles.section.bodyAttached')}
          </legend>

          {currentBody ? (
            <div className="rounded-md border border-border bg-muted/20 p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs text-muted-foreground">{currentBody.code}</div>
                  <div className="font-medium text-foreground">
                    {t(`bodies.type.${currentBody.bodyType}`)}
                    {currentBody.capacityM3 != null && ` · ${Number(currentBody.capacityM3)} m³`}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {currentBody.isRented ? (
                      <>
                        {t('bodies.rented')}: <strong className="text-foreground">{Number(currentBody.monthlyRentSar)} {t('common.sarPerMonth')}</strong>
                        <span className="ms-2 text-muted-foreground">→ {currentBodyOwner?.fullNameEn} ({currentBodyOwner?.code})</span>
                      </>
                    ) : (
                      <>
                        {t('bodies.owned')} {t('common.by')} {currentBodyOwner?.fullNameEn} ({currentBodyOwner?.code})
                      </>
                    )}
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleUnassignBody} disabled={unassignMutation.isPending}>
                  <Unlink className="h-3 w-3" />
                  {t('bodies.unmount')}
                </Button>
              </div>
            </div>
          ) : showAssignBody ? (
            <div className="rounded-md border border-dashed border-border p-4 space-y-3">
              <Field label={t('bodies.selectAvailable')}>
                <Select value={selectedBodyId} onChange={setSelectedBodyId} placeholder={t('bodies.selectBody')}>
                  {availableBodies.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code} — {t(`bodies.type.${b.bodyType}`)}
                      {b.capacityM3 != null ? ` (${Number(b.capacityM3)} m³)` : ''}
                      {b.isRented ? ` · ${t('bodies.rented')} ${Number(b.monthlyRentSar)}/mo` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="flex gap-2">
                <Button type="button" variant="gold" size="sm" onClick={handleAssignBody} disabled={!selectedBodyId || assignMutation.isPending}>
                  {assignMutation.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                  {t('bodies.mount')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => { setShowAssignBody(false); setSelectedBodyId(''); }}>
                  {t('common.cancel')}
                </Button>
              </div>
              {availableBodies.length === 0 && (
                <p className="text-xs text-muted-foreground">{t('bodies.noAvailable')}</p>
              )}
            </div>
          ) : (
            <Button type="button" variant="outline" onClick={() => setShowAssignBody(true)}>
              <Plus className="h-4 w-4" />
              {t('bodies.mountBody')}
            </Button>
          )}
        </fieldset>
      )}
    </div>
  );

  const ComplianceTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Istimara */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.istimara')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.istimaraNumber')}>
            <Input dir="ltr" value={form.istimaraNumber ?? ''} onChange={(e) => update('istimaraNumber', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('vehicles.field.istimaraExpiry')} suffix={<ExpiryIndicator dateString={form.istimaraExpiry || null} />}>
            <Input type="date" value={form.istimaraExpiry ?? ''} onChange={(e) => update('istimaraExpiry', e.target.value)} />
          </Field>
        </div>
        {/* PR-VEH-FIX Issue 1 MAY 11 — Istemarah file upload + preview */}
        {initial?.id && <IstemarahCard vehicleId={initial.id} />}
        {/* PR-DVA MAY 11 — current driver assignment + change button */}
        {initial?.id && <DriverAssignmentCard vehicleId={initial.id} />}
      </fieldset>

      {/* Insurance */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.insurance')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.insuranceCompanyName')}>
            <Input value={form.insuranceCompanyName ?? ''} onChange={(e) => update('insuranceCompanyName', e.target.value)} placeholder="Tawuniya, Bupa..." />
          </Field>
          <Field label={t('vehicles.field.insurancePolicyNumber')}>
            <Input dir="ltr" value={form.insurancePolicyNumber ?? ''} onChange={(e) => update('insurancePolicyNumber', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('vehicles.field.insuranceExpiry')} suffix={<ExpiryIndicator dateString={form.insuranceExpiry || null} />}>
            <Input type="date" value={form.insuranceExpiry ?? ''} onChange={(e) => update('insuranceExpiry', e.target.value)} />
          </Field>
        </div>
      </fieldset>

      {/* Naqliat */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.naqliat')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.naqliatCardNumber')}>
            <Input dir="ltr" value={form.naqliatCardNumber ?? ''} onChange={(e) => update('naqliatCardNumber', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('vehicles.field.naqliatScope')}>
            <Select value={form.naqliatScope ?? ''} onChange={(v) => update('naqliatScope', (v || null) as NaqliatScope | null)} placeholder="—">
              {NAQLIAT_SCOPES.map((s) => <option key={s} value={s}>{t(`vehicles.naqliatScope.${s}`)}</option>)}
            </Select>
          </Field>
          <Field label={t('vehicles.field.naqliatCardIssueDate')}>
            <Input type="date" value={form.naqliatCardIssueDate ?? ''} onChange={(e) => update('naqliatCardIssueDate', e.target.value)} />
          </Field>
          <Field label={t('vehicles.field.naqliatCardExpiryDate')} suffix={<ExpiryIndicator dateString={form.naqliatCardExpiryDate || null} />}>
            <Input type="date" value={form.naqliatCardExpiryDate ?? ''} onChange={(e) => update('naqliatCardExpiryDate', e.target.value)} />
          </Field>
        </div>
      </fieldset>

      {/* Periodic inspection */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.inspection')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.inspectionCertificateNumber')}>
            <Input dir="ltr" value={form.inspectionCertificateNumber ?? ''} onChange={(e) => update('inspectionCertificateNumber', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('vehicles.field.inspectionStationName')}>
            <Input value={form.inspectionStationName ?? ''} onChange={(e) => update('inspectionStationName', e.target.value)} placeholder="TÜV, Saraf..." />
          </Field>
          <Field label={t('vehicles.field.inspectionLastDate')}>
            <Input type="date" value={form.inspectionLastDate ?? ''} onChange={(e) => update('inspectionLastDate', e.target.value)} />
          </Field>
          <Field label={t('vehicles.field.inspectionNextDueDate')} suffix={<ExpiryIndicator dateString={form.inspectionNextDueDate || null} />}>
            <Input type="date" value={form.inspectionNextDueDate ?? ''} onChange={(e) => update('inspectionNextDueDate', e.target.value)} />
          </Field>
          <Field label={t('vehicles.field.inspectionStatus')} className="md:col-span-2">
            <Select value={form.inspectionStatus ?? ''} onChange={(v) => update('inspectionStatus', (v || null) as InspectionStatus | null)} placeholder="—">
              {INSPECTION_STATUSES.map((s) => <option key={s} value={s}>{t(`vehicles.inspectionStatus.${s}`)}</option>)}
            </Select>
          </Field>
        </div>
      </fieldset>
    </div>
  );

  const TechnicalTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.technical')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.vin')} className="md:col-span-2">
            <Input
              dir="ltr"
              value={form.vin ?? ''}
              onChange={(e) => update('vin', e.target.value.toUpperCase())}
              className="font-mono uppercase"
              maxLength={17}
              placeholder="WDB9634032L123456"
            />
            <p className="text-[11px] text-muted-foreground mt-1">{t('vehicles.help.vin')}</p>
          </Field>
          <Field label={t('vehicles.field.engineNumber')}>
            <Input dir="ltr" value={form.engineNumber ?? ''} onChange={(e) => update('engineNumber', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('vehicles.field.fuelType')}>
            <Select value={form.fuelType ?? ''} onChange={(v) => update('fuelType', (v || null) as FuelType | null)} placeholder="—">
              {FUEL_TYPES.map((f) => <option key={f} value={f}>{t(`vehicles.fuelType.${f}`)}</option>)}
            </Select>
          </Field>
          <Field label={t('vehicles.field.transmission')}>
            <Select value={form.transmission ?? ''} onChange={(v) => update('transmission', (v || null) as TransmissionType | null)} placeholder="—">
              {TRANSMISSIONS.map((t2) => <option key={t2} value={t2}>{t(`vehicles.transmission.${t2}`)}</option>)}
            </Select>
          </Field>
          <Field label={t('vehicles.field.axlesCount')}>
            <Input
              type="number" min={2} max={10} dir="ltr"
              value={form.axlesCount ?? ''}
              onChange={(e) => update('axlesCount', e.target.value ? Number(e.target.value) : null)}
            />
          </Field>
          <Field label={t('vehicles.field.gvwrKg')}>
            <Input
              type="number" min={0} dir="ltr"
              value={form.gvwrKg ?? ''}
              onChange={(e) => update('gvwrKg', e.target.value ? Number(e.target.value) : null)}
              className="font-mono"
              placeholder="40000"
            />
          </Field>
          <Field label={t('vehicles.field.payloadCapacityKg')}>
            <Input
              type="number" min={0} dir="ltr"
              value={form.payloadCapacityKg ?? ''}
              onChange={(e) => update('payloadCapacityKg', e.target.value ? Number(e.target.value) : null)}
              className="font-mono"
              placeholder="25000"
            />
          </Field>
        </div>
      </fieldset>

      {/* Integrations */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.integrations')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.afaqyDeviceId')}>
            <Input dir="ltr" value={form.afaqyDeviceId ?? ''} onChange={(e) => update('afaqyDeviceId', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('vehicles.field.petroappCardId')}>
            <Input dir="ltr" value={form.petroappCardId ?? ''} onChange={(e) => update('petroappCardId', e.target.value)} className="font-mono" />
          </Field>
        </div>
      </fieldset>

      {/* Purchase */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('vehicles.section.purchase')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('vehicles.field.purchaseDate')}>
            <Input type="date" value={form.purchaseDate ?? ''} onChange={(e) => update('purchaseDate', e.target.value)} />
          </Field>
          <Field label={t('vehicles.field.purchasePriceSar')}>
            <Input
              type="number" step="0.01" min={0} dir="ltr"
              value={form.purchasePriceSar ?? ''}
              onChange={(e) => update('purchasePriceSar', e.target.value ? Number(e.target.value) : null)}
              className="font-mono"
              placeholder="350000.00"
            />
          </Field>
        </div>
      </fieldset>

      {/* Notes */}
      <Field label={t('vehicles.field.notes')}>
        <textarea
          value={form.notes ?? ''}
          onChange={(e) => update('notes', e.target.value)}
          rows={3}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </Field>
    </div>
  );

  const tabs = [
    {
      key: 'identity',
      label: <span className="inline-flex items-center gap-1.5"><IdCard className="h-4 w-4" /> {t('vehicles.tab.identity')}</span>,
      badge: !plateValidation.valid && form.plateLettersEn ? (
        <span className="text-[10px] text-destructive">●</span>
      ) : null,
      content: IdentityTab,
    },
    {
      key: 'ownership',
      label: <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> {t('vehicles.tab.ownership')}</span>,
      content: OwnershipTab,
    },
    {
      key: 'compliance',
      label: <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> {t('vehicles.tab.compliance')}</span>,
      content: ComplianceTab,
    },
    {
      key: 'technical',
      label: <span className="inline-flex items-center gap-1.5"><Wrench className="h-4 w-4" /> {t('vehicles.tab.technical')}</span>,
      content: TechnicalTab,
    },
    ...(initial?.id ? [{
      key: 'operating-costs',
      label: <span className="inline-flex items-center gap-1.5"><Wrench className="h-4 w-4" /> Operating costs</span>,
      content: <OperatingCostsTab vehicleId={initial.id} />,
    }] : []),
    ...(initial?.id ? [{
      key: 'documents',
      label: <span className="inline-flex items-center gap-1.5"><FileBadge className="h-4 w-4" /> Documents</span>,
      content: (
        <div className="p-6">
          <EntityDocumentsPanel scope="vehicle" entityId={initial.id} slots={VEHICLE_SLOTS} />
        </div>
      ),
    }] : []),
    ...(initial?.id ? [{
      key: 'ledger',
      label: <span className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> Ledger</span>,
      content: (
        <div className="p-6">
          <LedgerView subjectKind="vehicle" subjectId={initial.id} />
        </div>
      ),
    }] : []),
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <Tabs tabs={tabs} defaultKey="identity" />

      <div className="shrink-0 border-t border-border bg-muted/30">
        {error && (
          <p className="text-sm text-destructive p-3 mx-6 my-3 rounded bg-destructive/10 border border-destructive/30" role="alert">
            {error}
          </p>
        )}
        {import.meta.env.DEV && (
          <details className="mx-6 mt-2 rounded border border-amber-500/30 bg-amber-500/5 text-[11px] text-amber-700 dark:text-amber-300">
            <summary className="cursor-pointer px-2 py-1 select-none">
              dev: form state {plateValidation.valid ? '✓' : '✗ plate'} {form.investorId ? '✓' : '✗ inv'} {form.legalEntityId ? '✓' : '✗ le'}
            </summary>
            <pre className="px-2 pb-2 overflow-auto max-h-48 font-mono">
              {JSON.stringify({ plateValidation, investorId: form.investorId, legalEntityId: form.legalEntityId, vehicleType: form.vehicleType }, null, 2)}
            </pre>
          </details>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
          )}
          <div className="flex flex-col items-stretch sm:items-end gap-1">
            {/* Save stays enabled so clicking it always explains what is wrong.
                A disabled button gave the user no feedback at all and made
                creating a vehicle look impossible. */}
            <Button type="submit" variant="gold" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitLabel ?? t('common.save')}
            </Button>
            {!initial && !plateValidation.valid && (
              <p className="text-[11px] text-muted-foreground">
                {t('vehicles.help.fixPlateBeforeSave', { defaultValue: 'Enter a valid plate (4 digits + 3 letters) before saving' })}
              </p>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}

// PR-RIYADH-FIX-3 MAY 12 — Operating costs tab. Shows driver-paid expenses
// (driver_expenses rows tagged to this vehicle) so the vehicle owner / ops
// has a P&L view per asset. Date range defaults to the last 30 days.
function OperatingCostsTab({ vehicleId }: { vehicleId: string }) {
  const [items, setItems] = useState<Array<{
    id: string; code: string; expenseDate: string; category: string;
    amountSar: number; driverName: string; vendorName: string | null;
    status: string;
  }>>([]);
  const [totals, setTotals] = useState<{ approved: number; pending: number }>({ approved: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // PR-AFAQY-FUEL-XREF MAY 12 — vehicle's fuel efficiency over the last 90 days.
  const [eff, setEff] = useState<{
    fillsCount: number; intervalsWithKm: number;
    totalSar: number; totalLiters: number; totalKm: number | null;
    avgSarPerKm: number | null; avgLitersPer100km: number | null;
    confidence: 'high' | 'medium' | 'low' | 'none';
    recipeSource: 'vehicle_history' | 'vehicle_type_average' | 'global_average' | 'vehicle_type_default' | 'none';
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const from = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    apiClient.get<{
      items: Array<{ id: string; code: string; expenseDate: string; category: string; amountSar: number; driverName: string; vendorName: string | null; status: string }>;
      totalApprovedSar: number;
      totalPendingSar: number;
    }>(`/api/v1/vehicles/${vehicleId}/expenses?from=${from}`)
      .then((r) => {
        if (cancelled) return;
        setItems(r.data.items ?? []);
        setTotals({ approved: r.data.totalApprovedSar ?? 0, pending: r.data.totalPendingSar ?? 0 });
      })
      .catch((e) => { if (!cancelled) setError(extractErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });

    apiClient.get<typeof eff>(`/api/v1/vehicles/${vehicleId}/fuel-efficiency`)
      .then((r) => { if (!cancelled) setEff(r.data); })
      .catch(() => { if (!cancelled) setEff(null); });
    return () => { cancelled = true; };
  }, [vehicleId]);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-md border border-border p-3 bg-muted/10">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Approved (last 30 days)</div>
          <div className="text-2xl font-mono font-light mt-0.5">{totals.approved.toLocaleString(undefined, { minimumFractionDigits: 2 })} SAR</div>
        </div>
        <div className="rounded-md border border-border p-3 bg-muted/10">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Pending approval</div>
          <div className="text-2xl font-mono font-light mt-0.5 text-amber-700 dark:text-amber-400">{totals.pending.toLocaleString(undefined, { minimumFractionDigits: 2 })} SAR</div>
        </div>
      </div>

      {/* Fuel efficiency summary — last 90 days */}
      {eff && (
        <div className="rounded-md border border-border p-3 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-2">
            Fuel efficiency · last 90 days
            <span className={
              eff.confidence === 'high'   ? 'text-[10px] text-emerald-700 dark:text-emerald-400'
            : eff.confidence === 'medium' ? 'text-[10px] text-amber-700 dark:text-amber-400'
            : eff.confidence === 'low'    ? 'text-[10px] text-rose-700 dark:text-rose-400'
            :                                'text-[10px] text-muted-foreground italic'
            }>
              {eff.confidence === 'none' ? 'insufficient data' : `${eff.confidence} confidence`}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fills</div>
              <div className="font-mono">{eff.fillsCount}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total spent</div>
              <div className="font-mono">{eff.totalSar.toLocaleString(undefined, { maximumFractionDigits: 0 })} SAR</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">L/100 km</div>
              <div className="font-mono">{eff.avgLitersPer100km != null ? eff.avgLitersPer100km.toFixed(1) : '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">SAR/km</div>
              <div className="font-mono">{eff.avgSarPerKm != null ? eff.avgSarPerKm.toFixed(2) : '—'}</div>
            </div>
          </div>
          {eff.intervalsWithKm === 0 && eff.fillsCount > 0 && (
            <div className="text-xs text-muted-foreground">
              Has {eff.fillsCount} fills but no Afaqy km between them yet — efficiency
              numbers will appear as Afaqy data accumulates.
            </div>
          )}
        </div>
      )}

      {error && <div className="text-sm text-rose-700 dark:text-rose-400">{error}</div>}

      <div className="rounded-md border border-border overflow-hidden">
        <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
          Driver-paid expenses
        </div>
        {loading ? (
          <div className="p-4 text-sm text-muted-foreground text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground text-center">No driver-paid expenses in the last 30 days.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted/20 text-muted-foreground"><tr>
              <th className="text-left px-3 py-2 font-normal">Code</th>
              <th className="text-left px-3 py-2 font-normal">Date</th>
              <th className="text-left px-3 py-2 font-normal">Driver</th>
              <th className="text-left px-3 py-2 font-normal">Category</th>
              <th className="text-left px-3 py-2 font-normal">Vendor</th>
              <th className="text-right px-3 py-2 font-normal">Amount</th>
              <th className="text-left px-3 py-2 font-normal">Status</th>
            </tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono">{r.code}</td>
                  <td className="px-3 py-2">{r.expenseDate}</td>
                  <td className="px-3 py-2">{r.driverName}</td>
                  <td className="px-3 py-2 capitalize">{r.category}</td>
                  <td className="px-3 py-2">{r.vendorName ?? '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.amountSar.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ----- Helper components -----

function Field({
  label, required, className, children, suffix,
}: {
  label: string; required?: boolean; className?: string; children: React.ReactNode; suffix?: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <div className="flex items-center justify-between">
        <Label>
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
        {suffix}
      </div>
      {children}
    </div>
  );
}

function Select({
  value, onChange, children, placeholder,
}: {
  value: string; onChange: (v: string) => void; children: React.ReactNode; placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {children}
    </select>
  );
}

// PR-VEH-FIX Issue 1 MAY 11 — Istemarah upload + preview card.
// Fetches metadata on mount; renders empty-state with Upload button or
// filled-state with View / Replace.
interface IstemarahMeta {
  number: string | null;
  expiry: string | null;
  daysUntilExpiry: number | null;
  expiryStatus: 'ok' | 'expiring_soon' | 'expired' | 'no_date';
  file: { url: string; type: string | null; uploadedAt: string | null; uploadedByName: string | null } | null;
}
const API_BASE_VF = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

function IstemarahCard({ vehicleId }: { vehicleId: string }) {
  const [meta, setMeta] = useState<IstemarahMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await apiClient.get<IstemarahMeta>('/api/v1/vehicles/' + vehicleId + '/istemarah');
      setMeta(r.data);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, [vehicleId]);

  async function handleFile(f: File) {
    if (!['image/jpeg', 'image/png', 'application/pdf'].includes(f.type)) {
      setError('File must be JPEG, PNG, or PDF.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) { setError('File too large (10 MB max).'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const jwt = getStoredToken();
      const res = await fetch(API_BASE_VF + '/api/v1/vehicles/' + vehicleId + '/istemarah', {
        method: 'POST',
        headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || ('HTTP ' + res.status));
      }
      await refresh();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setUploading(false); }
  }

  async function viewFile() {
    if (!meta?.file) return;
    try {
      const jwt = getStoredToken();
      const res = await fetch(API_BASE_VF + meta.file.url, { headers: jwt ? { Authorization: 'Bearer ' + jwt } : {} });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError(extractErrorMessage(e)); }
  }

  const chip = meta?.expiryStatus === 'expired'
    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/30">EXPIRED · {Math.abs(meta?.daysUntilExpiry ?? 0)}d ago</span>
    : meta?.expiryStatus === 'expiring_soon'
      ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30">Expires in {meta?.daysUntilExpiry}d</span>
      : null;

  return (
    <div className="rounded-lg border border-border p-3 space-y-2 text-sm">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Document</div>
        {chip}
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground italic">Loading…</div>
      ) : meta?.file ? (
        <div className="flex items-center gap-3 flex-wrap">
          {meta.file.type?.startsWith('image/') ? (
            <AuthedImage
              src={meta.file.url}
              alt="Istemarah"
              className="w-16 h-16 rounded border border-border object-cover cursor-pointer"
              onClick={viewFile}
            />
          ) : (
            <div className="w-16 h-16 rounded border border-border bg-muted/40 inline-flex items-center justify-center text-[10px] uppercase text-muted-foreground">PDF</div>
          )}
          <div className="flex-1 min-w-[140px] text-xs space-y-0.5">
            <div className="font-medium">{meta.file.type ?? 'file'}</div>
            <div className="text-muted-foreground">
              Uploaded {meta.file.uploadedAt ? new Date(meta.file.uploadedAt).toLocaleString('en-GB') : '—'}
              {meta.file.uploadedByName ? ' by ' + meta.file.uploadedByName : ''}
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={viewFile}>View</Button>
            <label className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 h-9 text-sm font-medium hover:bg-muted cursor-pointer">
              Replace
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                className="sr-only"
                disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
              />
            </label>
          </div>
        </div>
      ) : (
        <label className="block rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center cursor-pointer hover:bg-amber-500/10">
          <div className="text-sm font-medium text-amber-700 dark:text-amber-400">Upload Istemarah</div>
          <div className="text-[11px] text-muted-foreground mt-1">JPG, PNG, or PDF · up to 10 MB</div>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
            className="sr-only"
            disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
          />
        </label>
      )}
      {uploading && <div className="text-xs text-muted-foreground italic">Uploading…</div>}
      {error && <div className="text-xs text-rose-700 dark:text-rose-400">{error}</div>}
    </div>
  );
}

// PR-DVA MAY 11 — current driver section + change/unassign buttons.
interface DriverAssignment {
  id: string; code: string;
  driver_id: string; driver_code: string; driver_name: string | null; driver_phone: string | null;
  effective_from: string; effective_to: string | null;
  duration_days: number;
}
interface DriverOpt { id: string; code: string; fullNameEn?: string | null; currentVehicleId?: string | null; currentVehicleCode?: string | null }

function DriverAssignmentCard({ vehicleId }: { vehicleId: string }) {
  const [history, setHistory] = useState<DriverAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [drivers, setDrivers] = useState<DriverOpt[]>([]);
  const [terminating, setTerminating] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await apiClient.get<{ items: DriverAssignment[] }>('/api/v1/vehicles/' + vehicleId + '/driver-history');
      setHistory(r.data.items ?? []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, [vehicleId]);

  useEffect(() => {
    if (!reassignOpen) return;
    apiClient.get<{ items?: DriverOpt[] }>('/api/v1/drivers?status=active&pageSize=500')
      .then((r) => setDrivers(((r.data as { items?: DriverOpt[] }).items) ?? []))
      .catch(() => setDrivers([]));
  }, [reassignOpen]);

  const active = history.find((h) => h.effective_to == null);
  const past = history.filter((h) => h.effective_to != null);

  async function unassign() {
    if (!active) return;
    if (!window.confirm('Unassign ' + active.driver_name + ' from this vehicle?')) return;
    setTerminating(true);
    try {
      await apiClient.delete('/api/v1/driver-vehicle-assignments/' + active.id, {
        data: { reason: 'Manual unassign from vehicle form' },
      });
      await refresh();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setTerminating(false); }
  }

  return (
    <div className="rounded-lg border border-border p-3 space-y-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Driver assignment</div>
        {active ? (
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setReassignOpen(true)}>Change driver</Button>
            <Button type="button" variant="ghost" size="sm" onClick={unassign} disabled={terminating} className="text-rose-700 dark:text-rose-400">
              {terminating ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Unassign
            </Button>
          </div>
        ) : (
          <Button type="button" variant="gold" size="sm" onClick={() => setReassignOpen(true)}>Assign driver</Button>
        )}
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground italic">Loading…</div>
      ) : active ? (
        <div className="space-y-0.5">
          <div className="font-medium">{active.driver_name ?? '—'}</div>
          <div className="text-xs text-muted-foreground font-mono">{active.driver_code}{active.driver_phone ? ' · ' + active.driver_phone : ''}</div>
          <div className="text-[11px] text-muted-foreground">Since {new Date(active.effective_from).toLocaleDateString('en-GB')} · {Math.floor(active.duration_days)} day(s)</div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">No driver assigned.</div>
      )}
      {past.length > 0 && (
        <div className="pt-2 border-t border-border">
          <button type="button" onClick={() => setShowHistory((s) => !s)} className="text-xs text-muted-foreground hover:text-foreground underline">
            {showHistory ? 'Hide' : 'Show'} history ({past.length})
          </button>
          {showHistory && (
            <ul className="mt-2 space-y-1 text-xs">
              {past.map((h) => (
                <li key={h.id} className="text-muted-foreground">
                  <span className="font-medium text-foreground">{h.driver_name}</span> · {h.driver_code} · {new Date(h.effective_from).toLocaleDateString('en-GB')} → {h.effective_to ? new Date(h.effective_to).toLocaleDateString('en-GB') : 'present'} · {Math.floor(h.duration_days)}d
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && <div className="text-xs text-rose-700 dark:text-rose-400">{error}</div>}

      <AssignmentSheet
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        title={active ? 'Change driver' : 'Assign driver'}
        description={active ? 'Closes the current assignment and creates a new one.' : 'Creates a new assignment for this vehicle.'}
        pickKind="driver"
        options={drivers.map((d) => ({
          id: d.id,
          label: d.code + ' · ' + (d.fullNameEn ?? '—'),
          hasActive: !!d.currentVehicleId && d.currentVehicleId !== vehicleId,
          activeOn: d.currentVehicleCode ?? undefined,
        }))}
        buildPayload={(driverId, args) => ({
          driverId,
          vehicleId,
          effectiveFrom: new Date(args.effectiveFrom).toISOString(),
          reason: args.reason || undefined,
        })}
        onSaved={refresh}
      />
    </div>
  );
}
